import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { comics } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryComics = category
    ? comics.filter((c) => c.categorySlug === slug)
    : [];

  useEffect(() => {
    if (category) {
      document.title = `${category.name}漫画_免费在线${category.name}漫画阅读_89890漫画网`;
    } else {
      document.title = '漫画分类_89890漫画网';
    }

    const existing = document.getElementById('schema-category');
    if (existing) existing.remove();
    if (category) {
      const script = document.createElement('script');
      script.id = 'schema-category';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${category.name}漫画 - 89890漫画网`,
        description: `89890漫画网${category.name}漫画分类，提供免费高清${category.name}漫画在线阅读。${category.description}`,
        url: `https://www.89890.com/category/${slug}`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.89890.com' },
            { '@type': 'ListItem', position: 2, name: `${category.name}漫画`, item: `https://www.89890.com/category/${slug}` },
          ],
        },
      });
      document.head.appendChild(script);
    }

    return () => {
      const s = document.getElementById('schema-category');
      if (s) s.remove();
    };
  }, [category, slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900">分类未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium whitespace-nowrap">{category.name}漫画</span>
            </nav>

            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-primary-100 flex items-center justify-center">
                <i className={`${category.icon} text-2xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold">
                  {category.name}漫画
                </h1>
                <p className="mt-1 text-sm text-foreground-500">{category.description}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300 hover:text-primary-500'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-10 md:py-14">
          {categoryComics.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {categoryComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  data-product-shop=""
                >
                  <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - ${category.name}漫画`}
                      title={`${comic.title} - 免费在线${category.name}漫画阅读`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className="px-2.5 py-1 text-xs font-medium bg-primary-500/90 text-background-50 rounded-full whitespace-nowrap">
                        {comic.status}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h2 className="text-base font-heading text-foreground-900 font-bold group-hover:text-primary-500 transition-colors line-clamp-1">
                      {comic.title}
                    </h2>
                    <p className="mt-1.5 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {comic.description}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-400"></i>
                          {comic.rating}
                        </span>
                        <span>{(comic.views / 10000).toFixed(0)}万阅</span>
                      </div>
                      <span className="text-xs text-foreground-400">{comic.author}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300"></i>
              <p className="mt-4 text-foreground-500">该分类暂无漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}